<template>
    <v-container fluid>
        <v-form @submit.prevent="save">
            <v-card class="mb-5 pa-2">
                <card-header
                    :title="
                        $t('plan-proposal.form.texts.title', {
                            'title-prefix': $t(
                                'plan-proposal.breadcrumbs.create',
                            ),
                        })
                    "
                    :route="{
                        name: 'plan-proposal.index',
                        params: { customer_id: customer_id },
                    }"
                />

                <v-card-text>
                    <v-row>
                        <v-col>
                            <customer-details
                                v-if="customer"
                                :customer="customer"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="4">
                            <model-select
                                v-model="planPackage"
                                name="plan_package"
                                :model="planPackageModel"
                                :item-text="'name'"
                                :item-value="'id'"
                                :filters="{
                                    channel: false,
                                }"
                                :with-relations="['modules.module', 'modules.courtesy']"
                                :label="
                                    $t(
                                        'resources.planPackage.form.labels.package',
                                    )
                                "
                                @change="handlePlanPackageChange"
                            />
                        </v-col>
                    </v-row>

                    <v-row v-if="planProposal.planPackage">
                        <v-col>
                            <loyalty-selector
                                v-model="planProposal.proposal.loyalty"
                                :discount="
                                    planProposal.planPackage
                                        .default_anual_discount
                                "
                                :discount-code="discountCode"
                                :selectable="askLoyalty"
                                :show="showLoyaltyCondition"
                                @input="calculateDiscount()"
                            />

                            <module-selector
                                ref="moduleSelector"
                                :modules="optionalModules"
                                :plan-package-id="planProposal.planPackage.id"
                                :fixed-modules="fixedModules"
                                :active-module-ids="activeModuleIds"
                                @checked="checked"
                                @addAmount="addAmount"
                            />
                        </v-col>

                        <v-col cols="4">
                            <plan-proposal-summary
                                :key="summaryKey"
                                :plan-package="planProposal.planPackage"
                                :total-price="planProposal.total_price"
                                :final-price="planProposal.final_price"
                                :discount="planProposal.proposal.discount"
                                :loyalty="planProposal.proposal.loyalty"
                                :renovation="planProposal.proposal.renovation"
                                :configuration="
                                    planProposal.proposal.configuration
                                "
                                :fixed-modules="fixedModules"
                                :add-on-selected="addOnSelected"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <form-actions :loadingSave="loading"> </form-actions>
            </v-card>
        </v-form>
    </v-container>
</template>

<script>
import CustomerModel from '@/models/Customer'
import PlanPackageModel from '@/models/plans/PlanPackage'
import CardHeader from '@/components/utils/CardHeader'
import CustomerDetails from '@/components/common/CustomerDetails'
import FormActions from '@/components/utils/FormActions'
import { usePlanProposalManager } from '@/shared/services/usePlanProposalManager'

// Lazy loading de componentes pesados
const PlanProposalSummary = () =>
    import('@/shared/components/planProposal/PlanProposalSummary.vue')
const LoyaltySelector = () =>
    import('@/shared/components/planProposal/LoyaltySelector.vue')
const ModuleSelector = () =>
    import('@/shared/components/planProposal/ModuleSelector.vue')
const ModelSelect = () => import('../../shared/inputs/ModelSelect.vue')

export default {
    props: {
        customer_id: {
            required: true,
        },
    },

    components: {
        CardHeader,
        CustomerDetails,
        FormActions,
        ModelSelect,
        PlanProposalSummary,
        LoyaltySelector,
        ModuleSelector,
    },

    data: () => {
        return {
            customer: null,
            planPackage: null,
            planPackageModel: new PlanPackageModel(),
            discountCode: null,
            loading: false,
            activeModuleIds: [],
        }
    },

    created() {
        this.getCustomer()
        this.loadCustomerPlan()
        this.initManager()
    },

    methods: {
        async getCustomer() {
            this.customer = await new CustomerModel().find(this.customer_id)
        },

        async loadCustomerPlan() {
            try {
                const res = await this.$http.get(`config-plans/${this.customer_id}`)
                this.activeModuleIds = res.data.activeModuleIds || []
            } catch (error) {
                console.error('Error loading customer plan:', error)
            }
        },

        initManager() {
            const manager = usePlanProposalManager({
                vue: this,
                discountCode: { value: this.discountCode },
            })

            const descriptors = Object.getOwnPropertyDescriptors(manager)
            Object.keys(descriptors).forEach(key => {
                Object.defineProperty(this, key, descriptors[key])
            })
        },

        /**
         * Handler para mudança de pacote de plano
         * Usa o método do composable internamente
         */
        handlePlanPackageChange() {
            if (!this.planPackage) return
            this.selectPlanPackage(this.planPackage)
        },

        async save() {
            this.loading = true

            let clone = this.planProposal.clone()

            clone.plan_package_id = this.planProposal.planPackage.id
            clone.customer_id = this.customer_id
            clone.expire_at = this.$moment()
                .add(3, 'days')
                .toDate()
            clone.requested_at = this.$moment().toDate()

            let reseller = this.$store.getters['reseller/reseller']
            clone.consultant_name = reseller.name

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('plan-proposal.form.bus.save-success'),
            )

            this.$router.push({
                name: 'plan-proposal.index',
                params: { customer_id: this.customer_id },
            })
        },
    },
}
</script>
